import SearchableDropdown from '../../inputs/SearchableDropdown';
import styled, { css, keyframes } from 'styled-components';

// Styles
import { hoverCapable } from '../../../styles/hover';
import { reducedMotion } from '../../../styles/motion';

// Components
import { MainButton } from '../../inputs/buttons/MainButton';
import { Typography } from '../../display/Typography';

/**
 * V4 D3 / P10 TimerOverlay — refactor v4 2026-05-27.
 *
 * Trigger híbrido alineado con spec FR-10.1 ("reuso del componente
 * existente") + investigación industria control horario:
 *
 *  - IDLE: `<ButtonIconBackground variant="clock">` — REUSO del
 *    componente shared (round garnet 3.4rem, igual que los iconos
 *    de filtro/acción de tabla). Icono interno 2.4rem para que se
 *    lea como "pulsable" en aislamiento del header. Si hay tramo
 *    activo en otro RV no seleccionado, badge `ActiveDot` como
 *    child (Container es position:relative, el dot se ancla solo).
 *
 *  - ACTIVE: `TimerTriggerPill` local — pill horizontal garnet con
 *    `[⏱] HH:MM:SS` dentro. Toggl/Clockify/Harvest signature.
 *    NO reusable porque su comportamiento (anchura variable,
 *    contenido textual) no encaja en `ButtonIconBackground` cuyo
 *    contrato es 3.4×3.4 fijo. Pero conserva tokens del theme
 *    (colors.garnet, borderRadius.full) y el patrón de tooltip
 *    `data-tooltip + pseudo-elements` idéntico al shared.
 *
 *  - El switch idle↔pill ocurre cuando `activeSegment?.startDate`
 *    existe. La pill se hace ver, el idle es discreto — exactamente
 *    el contraste que comunica "está pasando algo AHORA mismo".
 *
 * Popover idéntico a `DropDownUserMenu` (gap 1.8rem, max-width
 * 23.6→36rem, HorizontalLine entre secciones, sin tintes de fondo).
 */

// ─────────────────────────────────────────────────────────────
// Wrapper de posicionamiento del popover.
// ─────────────────────────────────────────────────────────────

export const ContainerOverlay = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// ─────────────────────────────────────────────────────────────
// TimerTriggerPill — solo ACTIVE state (Toggl-style horizontal
// pill con tiempo). En IDLE NO se usa esto, se usa el
// `ButtonIconBackground variant="clock"` shared.
// ─────────────────────────────────────────────────────────────

export const TimerTriggerPill = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  position: relative;
  height: 3.4rem;
  padding: 0 1.4rem 0 1.2rem;
  background-color: ${({ theme }) => theme.colors.garnet};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  cursor: pointer;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  transition: opacity 0.15s ease-out;

  /* Fix 2026-07-04 (feedback usuario): el texto del chrono se re-renderiza
     cada segundo y el <p> de Typography devolvía el cursor a auto/text de
     forma intermitente — forzamos pointer en todos los hijos. */
  & * {
    cursor: pointer;
  }

  /* El guard táctil no es opcional: en la tablet el :hover se aplica en el
     touchstart y no hay ningún evento que lo quite, así que la pill se
     quedaba atenuada tras el toque. Regla dura del monorepo
     (.claude/rules/hover-tactil.md) — y la E2E de escritorio NO lo ve. */
  ${hoverCapable} {
    &:hover {
      opacity: 0.92;
    }
  }

  /* focus-visible-only: outline solo con navegación por teclado
     (no tras click). Mismo patrón aplicado al variant clock de
     ButtonIconBackground. */
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.black};
  }

  /* Tooltip nativo (mismo patrón que ButtonIconBackground). Va dentro del
     guard por lo mismo: en táctil se quedaba colgado sobre el trigger después
     de tocarlo. tooltipText se anula con el popover abierto, así que sin el
     guard solo se veía al CERRARLO — un tooltip que aparece al terminar. */
  ${hoverCapable} {
    &[data-tooltip]:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      top: calc(100% + 0.8rem);
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.88);
      color: white;
      padding: 0.6rem 1.1rem;
      border-radius: 0.5rem;
      font-size: 1rem;
      white-space: nowrap;
      z-index: 1000;
      pointer-events: none;
      box-shadow: 0 0.3rem 0.8rem rgba(0, 0, 0, 0.25);
      font-weight: 500;
      letter-spacing: 0.02rem;
    }
    &[data-tooltip]:hover::before {
      content: '';
      position: absolute;
      top: calc(100% + 0.1rem);
      left: 50%;
      transform: translateX(-50%);
      border: 0.5rem solid transparent;
      border-bottom-color: rgba(0, 0, 0, 0.88);
      z-index: 1000;
      pointer-events: none;
    }
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    height: 3.2rem;
    padding: 0 1.2rem 0 1rem;
    gap: 0.6rem;
  }
`;

export const PillIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  & > svg {
    width: 100%;
    height: 100%;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    width: 1.6rem;
    height: 1.6rem;
  }
`;

// ─────────────────────────────────────────────────────────────
// Badge verde pulsante — child del `ButtonIconBackground` en modo
// idle cuando hay tramo activo en otro RV no seleccionado. En
// modo pill (active) NO se renderiza (la pill ya ES el indicador).
//
// 🔴 Late 3 veces al aparecer y se queda verde fijo. En bucle infinito
// tenía el WebView del APK dibujando a 60 fps (154-194 % de CPU) horas
// enteras, y una tablet acabó en «ROIS no responde» (2026-10-08). Como
// se monta con `{showIdleBadge && …}`, vuelve a latir cada vez que
// reaparece. Ver `src/styles/motion.ts`.
// ─────────────────────────────────────────────────────────────

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.6); }
  70% { box-shadow: 0 0 0 6px rgba(40, 167, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
`;

export const ActiveDot = styled.span`
  position: absolute;
  top: 0.3rem;
  right: 0.3rem;
  width: 0.9rem;
  height: 0.9rem;
  background: ${({ theme }) => theme.colors.green};
  border: 0.15rem solid ${({ theme }) => theme.colors.greyVerySoft};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  animation: ${pulse} 1.6s 3;
  pointer-events: none;

  ${reducedMotion} {
    animation: none;
  }
`;

// ─────────────────────────────────────────────────────────────
// Popover panel — anatomía idéntica a DropDownUserMenu.Container.
// ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-0.4rem); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Popover = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  /* Anchura adaptativa: min = UserMenu (23.6rem) para mantener el
     peso visual cuando no hay contenido largo; cap = 36rem para
     dar cabida al ROID completo del SR; respeta la viewport en
     mobile. */
  min-width: 23.6rem;
  max-width: min(36rem, calc(100vw - 2rem));
  width: max-content;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  padding: ${({ theme }) => theme.spacing.m} 3rem
    ${({ theme }) => theme.spacing.m} ${({ theme }) => theme.spacing.m};
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xs};
  z-index: ${({ theme }) => theme.zIndex.popover};
  animation: ${fadeIn} 0.15s ease-out;
`;

export const HorizontalLine = styled.div`
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greySoftLight};
`;

// Fix 2026-07-20 (feedback usuario): el panel del overlay es `width:
// max-content` — cuando la línea de info (PK · sentido · carril) lo estira,
// los selectores se quedaban en el ancho fijo de su `inputSize` y quedaban
// cortos. `&&` gana a la clase interna del Container del dropdown.
export const OverlayDropdown = styled(SearchableDropdown)`
  && {
    width: 100%;
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    gap: 0.4rem;
  }
`;

export const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.xs}rem;
`;

/* Los botones del popover ocupan el ancho de la columna con el texto
   centrado — antes quedaban estirados con el texto pegado a la izquierda
   (el `Container` de MainButton no fija justify-content). */
export const ActionButton = styled(MainButton)`
  width: 100%;
  justify-content: center;
`;

/* Texto que NO cuenta para el ancho del Popover (`width: max-content`):
   envuelve al ancho que dictan el desplegable y las secciones en vez de
   estirar el panel. `width: 0 + min-width: 100%` lo excluye del cálculo
   intrínseco y luego lo deja ocupar toda la columna. */
const wrapsToPanelWidth = css`
  width: 0;
  min-width: 100%;
`;

/* Hint de estado ("Sin tramo activo" / "cronómetro activo en otro RV") —
   feedback usuario 2026-07-21: el párrafo largo del hint empujaba el ancho
   intrínseco hasta el cap de 36rem y el popover se AGRANDABA solo en el
   estado sin botones (con botones se quedaba compacto → tamaño
   inconsistente). Así el popover mide LO MISMO con y sin botones. */
export const HintText = styled(Typography)`
  ${wrapsToPanelWidth}
`;

/* Línea del tramo («PK · sentido · carril») — feedback usuario 2026-09-30
   (tablet EXSER 2): con carril la línea era más ancha que el desplegable y
   estiraba el panel, que se veía más ancho que el desplegable; sin carril
   quedaba compacto. Ahora el panel mide lo mismo y, si no cabe, la línea baja
   a la siguiente. */
export const SegmentInfoText = styled(Typography)`
  ${wrapsToPanelWidth}
`;
